import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons'
import { Box, Collapse, Flex, IconButton, Text, VStack } from '@chakra-ui/react'
import { usePopUpMessage } from '@hb/react'
import {
  AutoDisplayCondition,
  autoDisplayConditionText,
  CONSENT_FORM_TEMPLATES,
  ConsentFormTemplate,
  DropdownField,
  ErrorWithMessage,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  getAutoDisplayConditionKey,
  getConditionOverrides,
  getInsurancePlanField,
  getPracticeConsentFormsCollectionPath,
  insuranceProviderField,
  plannedDeliveryLocationField,
} from '@hb/shared'
import { FORM_ERROR, ValidationErrors } from 'final-form'
import { updateDoc } from 'firebase/firestore'
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { emptyUseDocument } from '../../../backend/db/constants'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import { UseDocument } from '../../../backend/db/types'
import { DeleteButton } from '../../Buttons'
import { SolidActionButton } from '../../Buttons/ActionButton'
import { SimpleForm } from '../../forms/FinalForm/SimpleForm'
import { Loading } from '../../Loading'
import { DefaultModal } from '../../Modals'
import { AutoDisplayConditionView, ConditionDivider } from './AutoDisplayCondition'

/*
Consent Form Settings
 - Visible for all patients
 - Visible for medicaid patients
 - Visible for non-medicaid patients
 - Visible for x insurer/plan
*/

interface ConsentFormSettingsContextType {
  consentForm: UseDocument<ConsentFormTemplate>
  onClose: () => void
}

const ConsentFormSettingsContext = createContext<ConsentFormSettingsContextType>({
  consentForm: emptyUseDocument,
  onClose: () => {},
})

const ConsentFormSettingsProvider: FC<
  PropsWithChildren<{
    consentFormId: string | null
    onClose: () => void
    practiceId: string | null
  }>
> = ({ consentFormId, onClose, children, practiceId }) => {
  const collection = useMemo(
    () => (practiceId ? getPracticeConsentFormsCollectionPath(practiceId) : CONSENT_FORM_TEMPLATES),
    [practiceId],
  )
  const consentForm = useDocument<ConsentFormTemplate>(collection, consentFormId)

  const contextValue = useMemo(() => ({ consentForm, onClose }), [consentForm, onClose])
  return (
    <ConsentFormSettingsContext.Provider value={contextValue}>
      {children}
    </ConsentFormSettingsContext.Provider>
  )
}

const consentFormAutoDisplayField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Select an option',
  searchable: true,
  options: [
    { id: 'all', text: autoDisplayConditionText.all },
    { id: 'medicaid', text: autoDisplayConditionText.medicaid },
    { id: 'non-medicaid', text: autoDisplayConditionText['non-medicaid'] },
    { id: 'self-pay', text: autoDisplayConditionText['self-pay'] },
    { id: 'insurer', text: autoDisplayConditionText.insurer },
    { id: 'insurance-plan', text: autoDisplayConditionText['insurance-plan'] },
    { id: 'delivery-location', text: autoDisplayConditionText['delivery-location'] },
  ],
}

const autoDisplayInsurancePlanField = getInsurancePlanField(false, 'insurerId')

const autoDisplayConditionField: FieldMap = {
  name: 'Display Condition',
  children: {
    type: consentFormAutoDisplayField,
    insurerId: {
      ...insuranceProviderField,
      condition: v => v?.type === 'insurer' || v?.type === 'insurance-plan',
    },
    insurancePlanId: {
      ...autoDisplayInsurancePlanField,
      condition: v => v?.type === 'insurance-plan',
    },
    deliveryLocation: {
      ...plannedDeliveryLocationField,
      searchable: true,
      condition: v => v?.type === 'delivery-location',
    },
  },
}

const AutoDisplayConditionInputPreview = ({
  condition,
  conditions,
  onEdit,
  onDelete,
}: {
  condition: AutoDisplayCondition
  conditions: AutoDisplayCondition[] | null
  onEdit: () => void
  onDelete: () => void
}) => {
  const overrides = useMemo(
    () => (conditions ? getConditionOverrides(conditions, condition) : []),
    [conditions, condition],
  )
  return (
    <Flex py={2} px={4} flexFlow="row" w="100%">
      <Flex flex={1} minW="0" alignItems="center">
        <AutoDisplayConditionView condition={condition} overrides={overrides} />
      </Flex>
      <Flex flexFlow="column" gap={1.5}>
        <IconButton
          size="xs"
          variant="solid"
          colorScheme="green"
          aria-label="Add Condition"
          icon={<EditIcon />}
          onClick={onEdit}
        />
        <IconButton
          size="xs"
          variant="solid"
          colorScheme="red"
          aria-label="Delete Condition"
          icon={<DeleteIcon />}
          onClick={onDelete}
        />
      </Flex>
    </Flex>
  )
}

const AutoDisplayConditionInput = ({
  condition,
  conditions,
  onSubmit,
  onDelete,
}: {
  condition: AutoDisplayCondition | null
  conditions: AutoDisplayCondition[] | null
  onSubmit: (condition: AutoDisplayCondition) => ValidationErrors
  onDelete: () => void
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const handleSubmit = useCallback(
    async (data: FieldMapValue) => {
      const { type, insurerId, insurancePlanId, deliveryLocation } = data
      if (!type) {
        return { [FORM_ERROR]: 'Please select a condition' }
      }
      switch (type) {
        case 'insurer':
          if (!insurerId) {
            return { [FORM_ERROR]: 'Insurer is required' }
          }
          return onSubmit({ type, insurerId })
        case 'insurance-plan':
          if (!insurerId) {
            return { [FORM_ERROR]: 'Insurer is required' }
          }
          if (!insurancePlanId) {
            return { [FORM_ERROR]: 'Insurance plan is required' }
          }
          return onSubmit({ type, insurerId, insurancePlanId })
        case 'delivery-location':
          if (!deliveryLocation) {
            return { [FORM_ERROR]: 'Delivery location is required' }
          }
          return onSubmit({ type, deliveryLocation })
        default:
          return onSubmit({ type })
      }
    },
    [onSubmit],
  )

  return (
    <Flex w="100%" position="relative" flexDir="column" borderRadius={4} bg="white" boxShadow="md">
      {condition && !isEditing ? (
        <AutoDisplayConditionInputPreview
          condition={condition}
          conditions={conditions ?? []}
          onEdit={() => setIsEditing(true)}
          onDelete={onDelete}
        />
      ) : (
        <SimpleForm
          value={condition}
          field={autoDisplayConditionField}
          hideLabels
          hideHints
          boxProps={{ paddingBottom: 4 }}
          onSubmit={handleSubmit}
          hasResetButton={false}
          submitText="Add Condition"
          onCancel={() => {
            if (condition) setIsEditing(false)
            else onDelete()
          }}
        />
      )}
    </Flex>
  )
}

const ConsentFormAutoDisplaySettings = () => {
  const {
    consentForm: { data: consentForm, ref },
  } = useContext(ConsentFormSettingsContext)

  const { showSuccess, showError } = usePopUpMessage()
  const [creatingNewCondition, setCreatingNewCondition] = useState(false)
  const currentAutoDisplay = useMemo(() => {
    if (!consentForm?.autoDisplay) return []
    if (!Array.isArray(consentForm.autoDisplay)) return []
    return consentForm.autoDisplay
  }, [consentForm?.autoDisplay])

  const [editedSettings, setEditedSettings] = useState<AutoDisplayCondition[]>(
    consentForm?.autoDisplay ?? [],
  )

  const [saving, setSaving] = useState(false)
  const handleSave = useCallback(async (): Promise<void> => {
    try {
      if (!ref) throw new Error('No consent form selected')
      setSaving(true)
      await updateDoc(ref, 'autoDisplay', editedSettings)
      showSuccess('Changes saved')
    } catch (error) {
      showError((error as ErrorWithMessage).message)
    }
    setSaving(false)
  }, [editedSettings, ref, showSuccess, showError])

  const hasChanges = useMemo(() => {
    const curr = Array.isArray(consentForm?.autoDisplay) ? consentForm.autoDisplay : []
    if (curr.length !== editedSettings.length) return true
    return curr.some((c, i) => {
      const edited = editedSettings[i]
      if (!edited) return true
      return getAutoDisplayConditionKey(c) !== getAutoDisplayConditionKey(edited)
    })
  }, [consentForm?.autoDisplay, editedSettings])

  const handleAddCondition = useCallback(
    (value: AutoDisplayCondition): ValidationErrors => {
      const existing = editedSettings.find(
        c => getAutoDisplayConditionKey(c) === getAutoDisplayConditionKey(value),
      )
      if (existing) return { [FORM_ERROR]: 'Condition already exists' }
      setEditedSettings(prev => [...prev, value])
      setCreatingNewCondition(false)
      return undefined
    },
    [editedSettings],
  )

  const handleDeleteCondition = useCallback(async (conditionIndex: number): Promise<void> => {
    setEditedSettings(prev => {
      const newSettings = [...prev]
      newSettings.splice(conditionIndex, 1)
      return newSettings
    })
  }, [])

  const handleEditCondition = useCallback(
    (conditionIndex: number, value: AutoDisplayCondition): ValidationErrors => {
      const existing = editedSettings[conditionIndex]
      if (!existing) return { [FORM_ERROR]: 'Condition not found' }
      const conflict = editedSettings.find(
        (c, idx) =>
          idx !== conditionIndex &&
          getAutoDisplayConditionKey(c) === getAutoDisplayConditionKey(value),
      )
      if (conflict) return { [FORM_ERROR]: 'Condition already exists' }
      const newSettings = [...editedSettings]
      newSettings[conditionIndex] = value
      setEditedSettings(newSettings)
      return undefined
    },
    [editedSettings],
  )

  return (
    <Flex w="100%" flexDir="column">
      <Text fontSize="lg" fontWeight={500} opacity={0.8}>
        Automatically Displayed For:
      </Text>
      <Flex w="100%" flexDir="column" gap={2}>
        {editedSettings.length ? (
          <VStack w="100%" alignItems="flex-start" divider={<ConditionDivider />} spacing={0}>
            {editedSettings.map((condition, index) => (
              <AutoDisplayConditionInput
                key={index}
                condition={condition}
                conditions={editedSettings}
                onSubmit={value => handleEditCondition(index, value)}
                onDelete={() => handleDeleteCondition(index)}
              />
            ))}
          </VStack>
        ) : null}
        {creatingNewCondition ? (
          <>
            {editedSettings.length ? <ConditionDivider /> : null}
            <AutoDisplayConditionInput
              condition={null}
              conditions={editedSettings}
              onSubmit={value => handleAddCondition(value)}
              onDelete={async () => {
                setCreatingNewCondition(false)
                return { success: 'Condition deleted' }
              }}
            />
          </>
        ) : null}
        {!editedSettings.length && !creatingNewCondition ? (
          <Flex alignItems="center" w="100%">
            <Text flex={1} fontSize="lg" lineHeight={1} opacity={0.8}>
              No patients
            </Text>
            <IconButton
              size="sm"
              variant="solid"
              colorScheme="green"
              aria-label="Add Condition"
              icon={<EditIcon />}
              onClick={() => setCreatingNewCondition(true)}
            />
          </Flex>
        ) : (
          <Box py={1} w="100%">
            <SolidActionButton w="100%" size="sm" onClick={() => setCreatingNewCondition(true)}>
              <AddIcon mr={1.5} filter="drop-shadow(0 0 1px rgba(0, 0, 0, 0.5))" />
              Add Condition
            </SolidActionButton>
          </Box>
        )}
        <Collapse in={hasChanges} style={{ width: '100%' }}>
          <Flex w="100%" justifyContent="flex-end" gap={2}>
            <DeleteButton
              colorScheme="red"
              variant="outline"
              itemName="Changes"
              borderRadius={4}
              actionName="Discard"
              text="Discard Changes"
              size="sm"
              onDelete={() => setEditedSettings(currentAutoDisplay)}
            />
            <SolidActionButton size="sm" onClick={handleSave} isLoading={saving}>
              Save Changes
            </SolidActionButton>
          </Flex>
        </Collapse>
      </Flex>
    </Flex>
  )
}

const ConsentFormSettingsContent = () => {
  const {
    consentForm: { loading },
  } = useContext(ConsentFormSettingsContext)

  return (
    <Flex flexDir="column">
      <Flex w="100%" border="1px solid" borderColor="gray.200" p={2}>
        <Text fontSize="lg" fontWeight={500}>
          Consent Form Settings
        </Text>
      </Flex>
      <Flex bg="gray.50" py={2} px={4} w="100%" flexDir="column" gap={2}>
        {loading ? (
          <Loading text="Loading consent form settings..." />
        ) : (
          <ConsentFormAutoDisplaySettings />
        )}
      </Flex>
    </Flex>
  )
}

export const ConsentFormSettings: FC<{
  consentFormId: string | null
  practiceId: string | null
  onClose: () => void
}> = ({ consentFormId, practiceId, onClose }) => (
  <ConsentFormSettingsProvider
    consentFormId={consentFormId}
    onClose={onClose}
    practiceId={practiceId}>
    <ConsentFormSettingsContent />
  </ConsentFormSettingsProvider>
)

export const ConsentFormSettingsModal: FC<{
  consentFormId: string | null
  practiceId: string | null
  onClose: () => void
}> = ({ consentFormId, practiceId, onClose }) => (
  <DefaultModal
    overlayHeader
    size="xl"
    isOpen={!!consentFormId}
    onClose={onClose}
    render={() => (
      <ConsentFormSettings
        consentFormId={consentFormId}
        onClose={onClose}
        practiceId={practiceId}
      />
    )}
  />
)
